/**
 * Which forwarded audio actually carried a VOICE.
 *
 * `spokenSeconds` exists to be read against `billedSeconds`: Muse bills by the
 * audio minute, silence included, and the whole point of the hold gate and the
 * two stream lifetimes is to shrink the gap between the two numbers. Until this
 * module the client counted a quantum as "spoken" whenever it was forwarded —
 * gate open, not muted, not held for a read-back (FIX-F11) — which is a
 * statement about a BUTTON, not about a person. Someone who holds the button
 * and says nothing counts as having spoken for as long as they hold it, and
 * because forwarded seconds are also billed seconds, that made the admin page's
 * billed/spoken ratio bottom out at 1.00× exactly when it should have been at
 * its worst. A silent open microphone is the failure the ratio is supposed to
 * expose; it cannot be the thing that hides it.
 *
 * So the count is now ENERGY, not permission: a quantum counts when its RMS
 * exceeds `VOICE_SPEECH_RMS`, plus a hangover so the gaps inside a sentence
 * still count as speech. The RMS is the worklet's own, computed in the audio
 * thread from the captured samples BEFORE the gate has had a chance to replace
 * them with zeros, so it is the room as heard and never the silence that was
 * sent in its place.
 *
 * WHY A HANGOVER. Ordinary speech is not continuous. A stop consonant, the beat
 * between "move sample beta" and "to VP14", a breath — each is tens of
 * milliseconds under any threshold worth setting, and counting only the loud
 * quanta would report about two thirds of a sentence and call the rest silence.
 * 200 ms is longer than any gap inside a phrase and shorter than the pause
 * before someone starts a new one, so a sentence counts as one stretch and the
 * quiet after it does not.
 *
 * WHY NOT REUSE `ENDPOINT_RMS_THRESHOLD` (0.012, in `voiceSession.js`). That one
 * drives the "speaking" flourish in the modal and is deliberately set low: a
 * missed indicator is a cosmetic failure, so it errs towards reacting. This one
 * is a BILLING-ADJACENT MEASURE, and it errs the other way — a threshold that
 * counts room tone would reproduce the bug it replaces, more slowly.
 *
 * NOTHING HERE DECIDES WHAT GOES ON THE WIRE. The gate (`voiceHoldGate.js`)
 * still answers that question, unchanged, and zeros still leave every quantum
 * while the gate is shut or the session is muted — the Muse real-time ingress
 * rule (measured 2026-09-03: a one-second gap earns a 1008 close ~11.5 s later)
 * is untouched by this file. Only the COUNT changes.
 *
 * Pure — no clock, no DOM, no timers. Time is AUDIO time, carried by the sample
 * counts the caller already has, which is the same clock Muse's endpointer runs
 * on and the only one that stays honest when the main thread stalls.
 */

/** 24 kHz mono, the rate the whole voice path is fixed at. */
const DEFAULT_SAMPLE_RATE_HZ = 24000;

/** One AudioWorklet render quantum: 128 samples, ~5.33 ms at 24 kHz. */
export const RENDER_QUANTUM_SAMPLES = 128;

/**
 * A positive number from the build's environment, or the fallback.
 *
 * A deliberate twin of `envNumber` in `voiceTimers.js` rather than an import:
 * that one is not exported, and this module is required by a node test with a
 * real `process` as well as bundled into a browser where `process.env` is
 * whatever the bundler substituted. Neither may throw here — a missing or
 * nonsense value means "use the default", never "count nothing".
 */
function envNumber(name, fallback) {
  try {
    const env =
      typeof process !== "undefined" && process && process.env
        ? process.env
        : null;
    const value = Number(env ? env[name] : null);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  } catch {
    return fallback;
  }
}

/**
 * The RMS, as a fraction of full scale, above which a quantum is speech.
 *
 * 0.02 is about -34 dBFS. Measured against the bench fixtures: a quiet room on
 * the MacBook's own microphone sits well under it, and the spoken WAVs the dock
 * probes use sit an order of magnitude over it. It is a DEFAULT —
 * `VUE_APP_VOICE_SPEECH_RMS` moves it without a code change, because the right
 * number for a gloved hand in a noisy lab is a thing to measure there rather
 * than argue about here.
 */
export const VOICE_SPEECH_RMS = envNumber("VUE_APP_VOICE_SPEECH_RMS", 0.02);

/** How long after the last loud quantum still counts as the same speech. */
export const SPEECH_HANGOVER_MS = 200;

/**
 * A running speech meter over one continuous run of captured audio.
 *
 * Stateful by necessity — the hangover is a memory — and that state is exactly
 * one number, the audio elapsed since the last quantum that was over the
 * threshold. It is measured in SAMPLES, so nothing here can drift against a
 * main thread that stalls.
 *
 * @param {object} [options]
 * @param {number} [options.threshold=VOICE_SPEECH_RMS] RMS of full scale
 * @param {number} [options.hangoverMs=SPEECH_HANGOVER_MS]
 * @param {number} [options.sampleRate=24000]
 * @returns {{observe: function, reset: function, hangoverSamples: number}}
 */
export function createSpeechMeter({
  threshold = VOICE_SPEECH_RMS,
  hangoverMs = SPEECH_HANGOVER_MS,
  sampleRate = DEFAULT_SAMPLE_RATE_HZ,
} = {}) {
  const rate =
    Number.isFinite(sampleRate) && sampleRate > 0
      ? sampleRate
      : DEFAULT_SAMPLE_RATE_HZ;
  const level = Number.isFinite(threshold) && threshold >= 0
    ? threshold
    : VOICE_SPEECH_RMS;
  const hold = Number.isFinite(hangoverMs) && hangoverMs > 0 ? hangoverMs : 0;
  const hangoverSamples = Math.round((hold / 1000) * rate);

  // Audio since the last loud quantum. Starts at Infinity and not at 0, so the
  // first quantum of a run counts only if it is loud ITSELF: a meter that
  // opened inside its own hangover would credit the 200 ms before anyone had
  // said anything, once per gate press.
  let sinceLoud = Infinity;

  return {
    hangoverSamples,

    /**
     * One captured quantum. Returns the samples that count as speech: all of
     * them or none of them, decided at the quantum's leading edge.
     *
     * The whole quantum is credited or refused rather than split at the exact
     * hangover boundary, because a quantum is 5.3 ms and the arithmetic to do
     * better would be a partial sample count that no consumer of this number
     * can tell from a rounding error.
     *
     * @param {number} rms the worklet's RMS for this quantum, 0..1
     * @param {number} sampleCount its length in samples
     * @returns {number} `sampleCount`, or 0
     */
    observe(rms, sampleCount) {
      const count =
        Number.isFinite(sampleCount) && sampleCount > 0 ? sampleCount : 0;
      if (!count) return 0;

      // NaN is not loud. An absent or broken level reads as silence rather than
      // as speech: over-counting is the bug this module exists to remove.
      if (Number.isFinite(rms) && rms > level) {
        sinceLoud = 0;
        return count;
      }

      const within = sinceLoud < hangoverSamples;
      // Advanced whether or not it counted, because the hangover is about how
      // long ago the voice was, not about how much of it was credited.
      sinceLoud += count;
      return within ? count : 0;
    },

    /**
     * Forget the hangover.
     *
     * Called when the run of captured audio BREAKS — the gate shuts, the
     * session mutes for an apply, a read-back takes the microphone. Without it
     * a hangover opened by the last word before a press would still be open
     * when the button came back down minutes later, and the first quiet quantum
     * of the next hold would count as the tail of the previous sentence.
     */
    reset() {
      sinceLoud = Infinity;
    },
  };
}

/**
 * How much of a run of quanta counts as speech. The pure whole-run reading of
 * the same rule `createSpeechMeter` applies one quantum at a time — defined in
 * terms of it, so there is one rule and not two that can drift.
 *
 * This is the shape a test drives; `voiceSession.js` uses the meter directly,
 * because it sees one quantum at a time and must not buffer a session's worth
 * of levels to answer a question it can answer as it goes.
 *
 * @param {object} options
 * @param {number[]|number} options.rms one RMS per quantum, in order
 * @param {number} [options.threshold]
 * @param {number} [options.hangoverMs]
 * @param {number} [options.sampleRate=24000]
 * @param {number} [options.quantumSamples=128] the length each entry stands for
 * @returns {number} how many of the entries counted
 */
export function speechFrames({
  rms,
  threshold,
  hangoverMs,
  sampleRate = DEFAULT_SAMPLE_RATE_HZ,
  quantumSamples = RENDER_QUANTUM_SAMPLES,
} = {}) {
  const meter = createSpeechMeter({ threshold, hangoverMs, sampleRate });
  const levels = Array.isArray(rms) ? rms : rms == null ? [] : [rms];
  let counted = 0;
  for (let i = 0; i < levels.length; i++) {
    if (meter.observe(levels[i], quantumSamples) > 0) counted += 1;
  }
  return counted;
}

/**
 * The same answer in seconds, for a caller holding a run of levels rather than
 * a live meter. Rounded to a tenth, which is the resolution `spokenSeconds`
 * reports and the finest anything on screen distinguishes.
 */
export function speechSeconds(options = {}) {
  const sampleRate = Number.isFinite(options.sampleRate) && options.sampleRate > 0
    ? options.sampleRate
    : DEFAULT_SAMPLE_RATE_HZ;
  const quantumSamples = Number.isFinite(options.quantumSamples) &&
    options.quantumSamples > 0
    ? options.quantumSamples
    : RENDER_QUANTUM_SAMPLES;
  const frames = speechFrames({ ...options, sampleRate, quantumSamples });
  return Math.round(((frames * quantumSamples) / sampleRate) * 10) / 10;
}
